import { DashboardLayout } from "@/components/DashboardLayout";
import { useFeatureFlags, useToggleFeatureFlag } from "@/hooks/useFeatureFlags";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ToggleRight } from "lucide-react";
import { toast } from "sonner";
import { captureError, getErrorMessage } from "@/lib/error-message";

export default function FeatureFlagsPage() {
  const { data: flags, isLoading } = useFeatureFlags();
  const toggleMutation = useToggleFeatureFlag();

  const handleToggle = (moduleKey: string, enabled: boolean) => {
    toggleMutation.mutate(
      { moduleKey, enabled },
      {
        onSuccess: () => toast.success(`${moduleKey} ${enabled ? "enabled" : "disabled"}`),
        onError: (err) => {
          const msg = getErrorMessage(err, "Failed to update flag");
          captureError(err, "feature_flag_toggle", msg);
          toast.error(msg);
        },
      }
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-foreground">Feature Flags</h1>
          <p className="text-muted-foreground mt-1">
            Control which modules are visible to your team. Super admins always have full access.
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ToggleRight className="h-5 w-5" />
                Module Access
              </CardTitle>
              <CardDescription>
                Toggle modules on to make them available to all team members.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-border">
                {flags?.map((flag) => (
                  <div
                    key={flag.module_key}
                    className="flex items-center justify-between py-4 first:pt-0 last:pb-0"
                  >
                    <div>
                      <p className="text-sm font-medium text-foreground">{flag.label}</p>
                      <p className="text-xs text-muted-foreground">
                        Module key: <code className="bg-muted px-1 py-0.5 rounded text-xs">{flag.module_key}</code>
                      </p>
                    </div>
                    <Switch
                      checked={flag.is_enabled}
                      onCheckedChange={(checked) => handleToggle(flag.module_key, checked)}
                      disabled={toggleMutation.isPending}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
